import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  GraduationCap,
  Play,
  Clock,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Search,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const categoryLabels = {
  'credit_basics': 'Credit Basics',
  'dispute_strategy': 'Dispute Strategy',
  'funding_readiness': 'Funding Readiness',
  'business_credit': 'Business Credit'
};

// Course Card Component
const CourseCard = ({ course, onSelect }) => {
  const totalLessons = course.modules?.reduce((acc, m) => acc + (m.lessons || 0), 0) || 0;
  const totalDuration = course.modules?.reduce((acc, m) => {
    const mins = parseInt(m.duration) || 0;
    return acc + mins;
  }, 0) || 0;

  return (
    <div 
      className="elite-card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
      onClick={() => onSelect(course)}
      data-testid={`course-${course.course_id}`}
    >
      <div className="aspect-video bg-stone-100 relative overflow-hidden">
        <img 
          src={course.thumbnail || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600'} 
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3">
          <span className="px-2 py-1 bg-white/90 rounded text-xs font-medium text-stone-700">
            {categoryLabels[course.category] || course.category}
          </span>
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-semibold text-stone-900 mb-2 group-hover:text-emerald-600 transition-colors">
          {course.title}
        </h3>
        <p className="text-sm text-stone-500 line-clamp-2 mb-4">
          {course.description}
        </p>
        <div className="flex items-center justify-between text-sm text-stone-500">
          <div className="flex items-center gap-1">
            <BookOpen size={14} />
            <span>{course.modules?.length || 0} modules</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock size={14} />
            <span>{totalDuration} min</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Course Detail Modal
const CourseDetail = ({ course, onClose }) => {
  if (!course) return null;

  return (
    <div className="fixed inset-0 bg-[rgba(6,28,20,0.75)] z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="aspect-video bg-stone-100 relative">
          <img 
            src={course.thumbnail || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600'} 
            alt={course.title}
            className="w-full h-full object-cover"
          />
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[rgba(6,28,20,0.75)] text-white flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            ×
          </button>
        </div>
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 300px)' }}>
          <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">
            {categoryLabels[course.category] || course.category}
          </span>
          <h2 className="text-2xl font-bold text-stone-900 mt-3 mb-2">{course.title}</h2>
          <p className="text-stone-500 mb-6">{course.description}</p>

          <h3 className="font-semibold text-stone-900 mb-4">Course Modules</h3>
          <div className="space-y-3">
            {course.modules?.map((module, index) => (
              <div 
                key={module.module_id}
                className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">{module.title}</p>
                    <p className="text-sm text-stone-500">{module.lessons} lessons • {module.duration}</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-stone-400" />
              </div>
            ))}
          </div>

          <Button className="w-full btn-primary mt-6">
            <Play size={18} className="mr-2" />
            Start Course
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Academy Component
const Academy = () => {
  const { getAuthHeaders } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/academy/courses`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    if (selectedCategory !== 'all' && course.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return course.title.toLowerCase().includes(query) ||
             course.description.toLowerCase().includes(query);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="academy-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">FundElite Academy</h1>
          <p className="text-stone-500 mt-1">Learn credit repair, dispute strategies, and funding readiness</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList>
          <TabsTrigger value="all">All Courses</TabsTrigger>
          <TabsTrigger value="credit_basics">Credit Basics</TabsTrigger>
          <TabsTrigger value="dispute_strategy">Dispute Strategy</TabsTrigger>
          <TabsTrigger value="funding_readiness">Funding Readiness</TabsTrigger>
          <TabsTrigger value="business_credit">Business Credit</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Courses Grid */}
      {filteredCourses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map(course => (
            <CourseCard
              key={course.course_id}
              course={course}
              onSelect={setSelectedCourse}
            />
          ))}
        </div>
      ) : (
        <div className="elite-card p-12 text-center">
          <GraduationCap size={40} className="mx-auto mb-4 text-stone-300" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No Courses Found</h3>
          <p className="text-stone-500">Try adjusting your search or category filter.</p>
        </div>
      )}

      {/* Course Detail Modal */}
      {selectedCourse && (
        <CourseDetail 
          course={selectedCourse} 
          onClose={() => setSelectedCourse(null)} 
        />
      )}
    </div>
  );
};

export default Academy;
